import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { toast } from "react-toastify";
import api from "utils/api";
import Dropdown from "components/CustomDropdown/Dropdown";

function EditBanner() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: "",
    bannerType: "",
    status: "PENDING",
  });
  const [bannerImage, setBannerImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    fetchBannerData();
  }, [id]);

  const fetchBannerData = async () => {
    try {
      const response = await api.get(`/banners/${id}`);
      if (response.data.success) {
        const banner = response.data.banner;
        setFormData({
          title: banner.title || "",
          bannerType: banner.bannerType || "",
          status: banner.status || "PENDING",
        });
        setExistingImage(banner.bannerImage || "");
        setImagePreview(banner.bannerImage || "");
      } else {
        toast.error("Failed to fetch banner details");
        navigate("/banner");
      }
    } catch (error) {
      console.error("Error fetching banner:", error);
      toast.error(error.response?.data?.message || "Failed to fetch banner details");
      navigate("/banner");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setBannerImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error("Please enter banner title");
      return;
    }
    if (!formData.bannerType) {
      toast.error("Please select banner type");
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("bannerType", formData.bannerType);
      data.append("status", formData.status);

      // Only append new image if selected
      if (bannerImage) {
        data.append("bannerImage", bannerImage);
      }

      const response = await api.put(`/banners/${id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success(response.data.message || "Banner updated successfully");
        navigate("/banner");
      } else {
        toast.error(response.data.message || "Failed to update banner");
      }
    } catch (error) {
      console.error("Error updating banner:", error);
      toast.error(error.response?.data?.message || "Failed to update banner");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox
          py={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
              <ArgonTypography variant="h5" fontWeight="medium">
                Edit Banner
              </ArgonTypography>
              <ArgonButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => navigate("/banner")}
              >
                Back to List
              </ArgonButton>
            </ArgonBox>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Banner Image Upload */}
                <Grid item xs={12}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Banner Image
                  </ArgonTypography>
                  <ArgonBox>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{
                        display: "block",
                        padding: "10px",
                        border: "1px solid #d2d6da",
                        borderRadius: "8px",
                        width: "100%",
                        cursor: "pointer",
                      }}
                    />
                  </ArgonBox>
                  {imagePreview && (
                    <ArgonBox mt={2}>
                      <ArgonTypography variant="caption" color="text" mb={1}>
                        {bannerImage ? "New Image Preview:" : "Current Image:"}
                      </ArgonTypography>
                      <ArgonBox>
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            maxWidth: "300px",
                            maxHeight: "200px",
                            borderRadius: "8px",
                            border: "1px solid #d2d6da",
                          }}
                        />
                      </ArgonBox>
                    </ArgonBox>
                  )}
                </Grid>

                {/* Title */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Title *
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter banner title"
                  />
                </Grid>

                {/* Banner Type */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Banner Type *
                  </ArgonTypography>
                  <Dropdown
                    label=""
                    value={formData.bannerType}
                    options={["Promotional", "Educational", "Seasonal"]}
                    onChange={(e) => setFormData({ ...formData, bannerType: e.target.value })}
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Status
                  </ArgonTypography>
                  <Dropdown
                    label=""
                    value={formData.status}
                    options={["ACTIVE", "INACTIVE", "PENDING"]}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  />
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <ArgonButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate("/banner")}
                  disabled={loading}
                >
                  Cancel
                </ArgonButton>
                <ArgonButton type="submit" variant="gradient" color="info" disabled={loading}>
                  {loading ? "Updating..." : "Update Banner"}
                </ArgonButton>
              </ArgonBox>
            </form>
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EditBanner;
